import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from "react-router-dom";
import AdSenseBlock from "../components/AdSenseBlock";
import { AD_SLOTS } from "../constants/adSlots";
import { createRakutenLink } from "../utils/rakuten";
import { createAmazonLink } from '../utils/amazon';

const TrailRunningBasics = () => (
  <div style={{
    padding: '1rem',
    lineHeight: 1.5,
    textAlign: 'left',
    maxWidth: '820px',
    margin: '0 auto'
  }}>

    {/* SEO */}
    <Helmet>
      <title>初心者向けトレラン入門｜装備・大会選び・安全ガイド</title>
      <meta
        name="description"
        content="はじめてのトレランも安心。装備の選び方から安全マナー、大会選びまで初心者に必要なすべてを10分で学べます。"
      />
      <meta
        name="keywords"
        content="トレラン, トレイルランニング, 初心者, 装備, 大会選び, 安全マナー, シミュレーター"
      />

      {/* HowTo構造化データ */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "トレイルランニングの始め方ガイド",
            "url": "https://trailrunsim.com/basics",
            "description": "初心者向けに、装備の選び方、ペース配分、安全と補給、最初の大会選びなど、トレランを始めるために必要な基本ポイントを手順形式で解説します。",
            "totalTime": "PT10M",
            "estimatedCost": {
              "@type": "MonetaryAmount",
              "currency": "JPY",
              "value": "0"
            },
            "step": [
              { "@type": "HowToStep", "name": "装備の準備", "text": "シューズ・ザック・ウェア・ライト・携帯などの必須装備を整えましょう。" },
              { "@type": "HowToStep", "name": "ペース配分を理解", "text": "登りは歩き、下りは安全に、平坦は呼吸に合わせたペースで。" },
              { "@type": "HowToStep", "name": "補給と水分管理", "text": "エナジージェルやナッツ、電解質ドリンクなどを携帯。" },
              { "@type": "HowToStep", "name": "安全とマナー", "text": "無理をせず、ゴミは持ち帰り、登山者とのすれ違い時は声をかけましょう。" },
              { "@type": "HowToStep", "name": "大会選び", "text": "10〜20km程度の距離・制限時間に余裕のある大会からスタート。" }
            ]
          }
        `}
      </script>

      {/* FAQ構造化データ */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "初心者向けのトレラン大会はどれが良いですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "10〜20km程度で完走率が高く、制限時間に余裕のある大会がおすすめです。"
              }
            },
            {
              "@type": "Question",
              "name": "トレランの装備は何が必要ですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "滑りにくいシューズ、レインウェア、水分補給道具、携帯、ファーストエイドキットなどが基本装備です。"
              }
            }
          ]
        })}
      </script>

      {/* パンくず構造化データ */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "ホーム", "item": "https://trailrunsim.com/" },
            { "@type": "ListItem", "position": 2, "name": "トレラン入門", "item": "https://trailrunsim.com/basics" }
          ]
        })}
      </script>
    </Helmet>

    {/* タイトル */}
    <h2>トレイルランニングの基礎知識</h2>
    <p style={{ margin: '0.6rem 0 1.2rem', color: '#444' }}>
      トレイルランニングは、山道や林道などの自然道を走るアウトドアスポーツです。
      大自然の中を駆け抜ける爽快感とともに、標高差・気象条件・路面状況など、
      ロードランニングとは異なるチャレンジが魅力です。
    </p>

    <AdSenseBlock adSlot={AD_SLOTS.BASICS} />

    {/* ------------------------------ */}
    {/* 🍏 装備について（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '1.6rem' }}>装備について</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      トレイルでは転倒や悪天候に備える必要があり、装備の選択が安全性と快適性を大きく左右します。
    </p>

    <ul style={{ marginTop: '0.4rem' }}>
      <li><strong>シューズ：</strong>濡れた岩場でも滑りにくいグリップ性能が重要。</li>
      <li><strong>ザック：</strong>水分・補給・レインウェアを収納できる軽量モデルが最適。</li>
      <li><strong>ウェア：</strong>吸湿速乾素材＋レインジャケットは必携。</li>
      <li><strong>その他：</strong>ヘッドライト、ポール、ホイッスル、携帯、予備バッテリー、救急セット。</li>
    </ul>

    <div style={{
      marginTop: '1rem',
      padding: '0.8rem',
      background: 'rgba(255,255,255,0.6)',
      borderRadius: '10px'
    }}>
      <strong>必携装備について</strong>
      <p style={{ marginTop: '0.4rem', color: '#444' }}>
        大規模大会では必携装備が厳しく定められています。
        普段から使い慣れておくことで安全性とパフォーマンスが向上します。
      </p>
    </div>

    <p style={{ marginTop: '0.8rem' }}>
      初心者向けの手ごろなギア特集はこちら →{' '}
      <Link to="/AffordableGear">軽量・コンパクトで実用的なおすすめ装備</Link>
    </p>

    {/* ------------------------------ */}
    {/* 🍏 おすすめギア（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '2rem' }}>おすすめギア</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      初心者から経験者まで幅広く支持される定番ギアを紹介します。
    </p>

<ul style={{ marginTop: '0.4rem', listStyle: 'none', padding: 0 }}>

  {/* シューズ */}
  <li style={{ padding: '0.35rem 0', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
    <strong>シューズ：</strong>
    <span style={{ fontWeight: 600 }}>ALTRA オリンパス 275</span>
    <div style={{ fontSize: '0.78rem', color: '#555', marginTop: '2px' }}>
      幅広い層から信頼される軽量モデル。
    </div>

    {/* ▼ 楽天 / Amazon ボタン */}
    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
      <a
        href={createRakutenLink("ALTRA オリンパス 275 トレイルランニングシューズ")}
        target="_blank"
        rel="noreferrer"
        style={{
          flex: 1,
          maxWidth: '160px',
          padding: '0.35rem 0.6rem',
          background: '#f2f2f7',
          borderRadius: '6px',
          color: '#bf0000',
          fontWeight: 600,
          textDecoration: 'none',
          fontSize: '0.8rem',
          textAlign: 'center'
        }}
      >
        楽天で見る
      </a>

      <a
        href={createAmazonLink("ALTRA オリンパス 275 トレイルランニングシューズ")}
        target="_blank"
        rel="noreferrer"
        style={{
          flex: 1,
          maxWidth: '160px',
          padding: '0.35rem 0.6rem',
          background: '#f2f2f7',
          borderRadius: '6px',
          color: '#ff9900',
          fontWeight: 600,
          textDecoration: 'none',
          fontSize: '0.8rem',
          textAlign: 'center'
        }}
      >
        Amazonで見る
      </a>
    </div>
  </li>

  {/* ザック */}
  <li style={{ padding: '0.35rem 0', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
    <strong>ザック：</strong>
    <span style={{ fontWeight: 600 }}>PaaGo WORKS rush 11R</span>
    <div style={{ fontSize: '0.78rem', color: '#555', marginTop: '2px' }}>
      長距離でも快適なフィット感と収納力。
    </div>

    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
      <a
        href={createRakutenLink("PaaGo WORKS rush 11R ランニングパック")}
        target="_blank"
        rel="noreferrer"
        style={{
          flex: 1,
          maxWidth: '160px',
          padding: '0.35rem 0.6rem',
          background: '#f2f2f7',
          borderRadius: '6px',
          color: '#bf0000',
          fontWeight: 600,
          textDecoration: 'none',
          fontSize: '0.8rem',
          textAlign: 'center'
        }}
      >
        楽天で見る
      </a>

      <a
        href={createAmazonLink("PaaGo WORKS rush 11R ランニングパック")}
        target="_blank"
        rel="noreferrer"
        style={{
          flex: 1,
          maxWidth: '160px',
          padding: '0.35rem 0.6rem',
          background: '#f2f2f7',
          borderRadius: '6px',
          color: '#ff9900',
          fontWeight: 600,
          textDecoration: 'none',
          fontSize: '0.8rem',
          textAlign: 'center'
        }}
      >
        Amazonで見る
      </a>
    </div>
  </li>

  {/* GPSウォッチ */}
  <li style={{ padding: '0.35rem 0' }}>
    <strong>GPSウォッチ：</strong>
    <span style={{ fontWeight: 600 }}>GARMIN Forerunner 570</span>
    <div style={{ fontSize: '0.78rem', color: '#555', marginTop: '2px' }}>
      高精度でコスパに優れたランニングウォッチ。
    </div>

    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
      <a
        href={createRakutenLink("GARMIN Forerunner 570 GPSウォッチ")}
        target="_blank"
        rel="noreferrer"
        style={{
          flex: 1,
          maxWidth: '160px',
          padding: '0.35rem 0.6rem',
          background: '#f2f2f7',
          borderRadius: '6px',
          color: '#bf0000',
          fontWeight: 600,
          textDecoration: 'none',
          fontSize: '0.8rem',
          textAlign: 'center'
        }}
      >
        楽天で見る
      </a>

      <a
        href={createAmazonLink("GARMIN Forerunner 570 GPSウォッチ")}
        target="_blank"
        rel="noreferrer"
        style={{
          flex: 1,
          maxWidth: '160px',
          padding: '0.35rem 0.6rem',
          background: '#f2f2f7',
          borderRadius: '6px',
          color: '#ff9900',
          fontWeight: 600,
          textDecoration: 'none',
          fontSize: '0.8rem',
          textAlign: 'center'
        }}
      >
        Amazonで見る
      </a>
    </div>
  </li>

</ul>


    {/* ------------------------------ */}
    {/* 🍏 ペース配分（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '2rem' }}>ペース配分のポイント</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      トレイルは起伏が激しく、ロードと同じ感覚では走れません。
      以下の基本を押さえておくと、後半の失速を防げます。
    </p>

    <ul>
      <li><strong>登り：</strong>基本は歩く。無理をすると後半に響く。</li>
      <li><strong>下り：</strong>スピードを出しすぎず、安全第一。</li>
      <li><strong>平坦：</strong>呼吸が整う範囲でペースを上げる。</li>
    </ul>

    <p style={{ marginTop: '0.8rem', color: '#444' }}>
      コースプロフィール（高低図）を事前に確認し、どこで頑張るかを決めておくと安心です。
    </p>

    {/* ------------------------------ */}
    {/* 🍏 栄養補給（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '2rem' }}>栄養補給と水分管理</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      長時間走るトレランでは、こまめな補給が完走の鍵を握ります。
    </p>

    <ul>
      <li><strong>エネルギー補給：</strong>ジェル、ナッツ、バナナなど。</li>
      <li><strong>水分補給：</strong>1時間あたり200〜400mlが目安。</li>
      <li><strong>エイド活用：</strong>地元名物が出る大会もあり、気分転換にも最適。</li>
    </ul>

    {/* ------------------------------ */}
    {/* 🍏 安全とマナー（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '2rem' }}>安全とマナー</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      山では自分の身は自分で守るのが基本。他の登山者との共存も大切です。
    </p>

    <ul>
      <li><strong>すれ違い時：</strong>声かけ＋歩行で安全に。</li>
      <li><strong>ゴミは持ち帰る：</strong>自然保護の基本。</li>
      <li><strong>無理をしない：</strong>疲労やケガを感じたら撤退も選択肢。</li>
    </ul>

    <p style={{ marginTop: '0.8rem', color: '#444' }}>
      トレランは自然と対話するスポーツ。景色や空気、仲間との会話、エイドの温かさも楽しみのひとつです。
    </p>

    {/* ------------------------------ */}
    {/* 🍏 トレランを始めるには（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '2rem' }}>トレランを始めるには？</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      初心者はまず低山や整備されたトレイルからスタートするのがおすすめです。
    </p>

    <ul>
      <li>標高〜300m程度の低山から</li>
      <li>距離は5〜10km程度</li>
      <li>歩き中心でもOK。「楽しむ」ことを意識</li>
    </ul>

    {/* ------------------------------ */}
    {/* 🍏 メンタル（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '2rem' }}>メンタルの保ち方</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      トレランは長時間の孤独との戦いでもあります。気持ちが折れそうなときの対処法を知っておくと安心です。
    </p>

    <ul>
      <li>現在地（距離・標高）を把握する</li>
      <li>「次のピークまで」など小さな目標を設定</li>
      <li>補給で気分転換</li>
      <li>長い登りでも「いつかは終わる」と言い聞かせる</li>
    </ul>

    {/* ------------------------------ */}
    {/* 🍏 大会選び（Apple風） */}
    {/* ------------------------------ */}
    <h3 style={{ marginTop: '2rem' }}>大会選びのポイント</h3>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      初心者が最初に出る大会は、距離・標高・制限時間のバランスが重要です。
    </p>

    <ul>
      <li>距離：10〜20km</li>
      <li>累積標高：500〜1000m</li>
      <li>制限時間：ゆとりのある設定</li>
      <li>完走率：高い大会は安心感がある</li>
    </ul>

    <p style={{ marginTop: '0.8rem' }}>
      初レース選びには <Link to="/blog/first-trail-race">初レース体験談</Link> も参考になります。
    </p>

    <AdSenseBlock adSlot={AD_SLOTS.BASICS} />

  </div>
);

export default TrailRunningBasics;
